import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ProviderTransferCompanyType } from 'src/types/providerTransferCompany';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import TopBlock from './TopBlock';
import {
    clearProviderTransferCompanyDetail,
    createProviderTransferCompanyDetailFetch,
    deleteProviderTransferCompanyFetch,
    getProviderTransferCompanyDetailFetch,
    updateProviderTransferCompanyDetailFetch
} from 'src/resources/providerTransferCompanyDetail/reducer';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { ConstantsCodeType } from '../../types/constantsCodes';
import { getProvidersListFetch } from 'src/resources/providersList/reducer';

export type FormErrorsType = { [key: string]: boolean };

export type OnChangeType = (key: EditKeysType, value: any, error: boolean) => void;

type EditKeysType = 'TARGET_PROVIDER_ESHOP_KEY'

export const EDIT_KEYS = [
    'TARGET_PROVIDER_ESHOP_KEY'
];

const initData = {};
const ProviderTransferCompanyDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idProviderTransferCompany } = useParams();

    const newProviderTransferCompany = !idProviderTransferCompany;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.providerTransferCompanyDetail.data);
    const loading = useSelector((state: RootStoreType) => state.providerTransferCompanyDetail.loading);
    const deleted = useSelector((state: RootStoreType) => state.providerTransferCompanyDetail.deleted);
    const orderStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.orderStatusCodes);

    const providersData = useSelector((state: RootStoreType) => state.providersList.data);
    const providersCodes: ConstantsCodeType[] = providersData ? providersData.map((provider) => ({
        label: provider.COMPANY_NAME,
        value: provider.ESHOP_KEY
    }) as ConstantsCodeType) : [];
    const [editData, setEditData] = useState<ProviderTransferCompanyType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const useCheckPermissionCreate = useCheckPermission('PROVIDER_TRANSFER_COMPANY.CREATE');
    const useCheckPermissionUpdate = useCheckPermission('PROVIDER_TRANSFER_COMPANY.UPDATE');
    const disableEdit = newProviderTransferCompany ? !useCheckPermissionCreate : !useCheckPermissionUpdate;
    const useCheckPermissionDelete = useCheckPermission('PROVIDER_TRANSFER_COMPANY.DELETE');
    const disableDelete = newProviderTransferCompany || !useCheckPermissionDelete;

    const backUrl = '/settings/provider_transfer_companies/';

    useEffect(() => {
        if (idProviderTransferCompany) {
            dispatch(
                setLocationParams({
                    name: translate('providerTransferCompanyDetail.title', { idProviderTransferCompany }),
                    backUrl
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('providerTransferCompanyDetail.titleNew'), backUrl }));
        }
    }, [idProviderTransferCompany]);

    useEffect(() => {
        if (!providersData) {
            dispatch(getProvidersListFetch({}));
        }
    }, [providersData]);

    useEffect(() => {
        if (idProviderTransferCompany) {
            dispatch(getProviderTransferCompanyDetailFetch(idProviderTransferCompany));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearProviderTransferCompanyDetail());
        };
    }, [idProviderTransferCompany]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!idProviderTransferCompany && data?.ID) {
            navigate(`/settings/provider_transfer_companies/${String(data.ID)}/`);
        }
    }, [data]);

    useEffect(() => {
        if (deleted) {
            navigate(backUrl);
        }
    }, [deleted]);

    const onChange: OnChangeType = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleDeleteProviderTransferCompany = () => {
        setConfirmParams({
            title: translate('providerTransferCompanyDetail.deleteProviderTransferCompany'),
            content: translate('providerTransferCompanyDetail.deleteProviderTransferCompanyConfirm'),
            action: () => dispatch(deleteProviderTransferCompanyFetch(idProviderTransferCompany))
        });
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(newProviderTransferCompany ? 'providerTransferCompanyDetail.titleNew' : 'providerTransferCompanyDetail.title', { idProviderTransferCompany }),
                content: translate(
                    newProviderTransferCompany ? 'providerTransferCompanyDetail.createConfirm' : 'providerTransferCompanyDetail.saveConfirm'
                ),
                action: () => {
                    const formatData: Partial<ProviderTransferCompanyType> = { TARGET_PROVIDER_ESHOP_KEY: editData.TARGET_PROVIDER_ESHOP_KEY };

                    if (newProviderTransferCompany) {
                        dispatch(createProviderTransferCompanyDetailFetch(formatData));
                    } else {
                        dispatch(updateProviderTransferCompanyDetailFetch({
                            idProviderTransferCompany,
                            data: formatData
                        }));
                    }
                }
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock
                data={data}
                idProviderTransferCompany={idProviderTransferCompany}
                newProviderTransferCompany={newProviderTransferCompany}
                disableEdit={disableEdit}
                disableDelete={disableDelete}
                handleSave={handleSave}
                handleDeleteProviderTransferCompany={handleDeleteProviderTransferCompany}
            />
            <form ref={formRef}>
                {loading && <Loading />}

                <FormBlock>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        disableEdit={disableEdit}
                        orderStatusCodes={orderStatusCodes}
                        providersCodes={providersCodes}
                    />
                </FormBlock>
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default ProviderTransferCompanyDetailContainer;
